import { OnboardingSlideProps, SLIDE_BACKGROUNDS } from "../onboarding.types";
import { PaginationDots } from "../PaginationDots";

/**
 * Track Slide (Panel 2)
 *
 * Second onboarding screen explaining automatic activity tracking:
 * "Track Automatically" with wearable sync visualization.
 *
 * Design specs from panel2.html:
 * - Primary: uses app's primary color
 * - Background: #102210 (dark green)
 * - Glass card with backdrop-blur and gradient border
 */
export function TrackSlide({ onNext, onSkip }: OnboardingSlideProps) {
  return (
    <div
      className="relative h-dvh w-full overflow-hidden flex flex-col justify-between bg-[#102210] text-white"
      style={{ fontFamily: '"Lexend", sans-serif' }}
    >
      {/* Background Layer */}
      <div className="absolute inset-0 z-0 pointer-events-none">
        <img
          className="h-full w-full object-cover object-center opacity-60 scale-105"
          alt="Smart watch on a runner's wrist illuminated by neon city lights"
          src={SLIDE_BACKGROUNDS.track}
        />
        {/* Gradient overlays */}
        <div className="absolute inset-0 bg-gradient-to-b from-[#102210]/50 via-[#102210]/20 to-[#102210]" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#102210] via-transparent to-transparent opacity-90" />
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,transparent_0%,#102210_100%)] opacity-80" />
      </div>

      {/* Decorative Elements Layer */}
      <div className="absolute inset-0 z-10 pointer-events-none overflow-hidden">
        {/* Spinning Circles */}
        <div className="absolute left-1/2 top-[28%] -translate-x-1/2 -translate-y-1/2 flex items-center justify-center">
          <div className="h-[280px] w-[280px] rounded-full opacity-40 animate-[spin_20s_linear_infinite] border border-primary/10" />
          <div className="absolute h-[220px] w-[220px] rounded-full border-dashed opacity-60 animate-[spin_15s_linear_infinite_reverse] border border-primary/20" />
          <div className="absolute h-[140px] w-[140px] rounded-full blur-[60px] bg-primary/5" />
        </div>

        {/* Grid Pattern */}
        <div className="absolute top-0 right-0 w-full h-full bg-[linear-gradient(rgb(from_var(--color-primary)_r_g_b_/_0.03)_1px,transparent_1px),linear-gradient(90deg,rgb(from_var(--color-primary)_r_g_b_/_0.03)_1px,transparent_1px)] bg-[size:4rem_4rem] [mask-image:radial-gradient(circle_at_center,black_30%,transparent_70%)]" />
      </div>

      {/* Content Layer */}
      <div className="relative z-20 flex h-full flex-col justify-between px-4 pt-6 pb-6">
        {/* Top Row - Pagination & Skip */}
        <div className="flex w-full items-center justify-between pt-[calc(env(safe-area-inset-top)+0.5rem)]">
          <PaginationDots total={3} activeIndex={1} />
          <button
            onClick={onSkip}
            className="rounded-full border border-transparent px-4 py-2 text-xs font-bold uppercase tracking-widest text-white/50 transition-colors hover:border-white/10 hover:bg-white/5 hover:text-white active:scale-95"
          >
            Skip
          </button>
        </div>

        {/* Center Icon - Watch with Syncing Badge */}
        <div className="absolute left-1/2 top-[28%] -translate-x-1/2 -translate-y-1/2 transform text-center">
          <div className="relative inline-flex flex-col items-center gap-4">
            {/* Watch Icon Container */}
            <div className="relative flex h-20 w-20 items-center justify-center rounded-full bg-black/20 backdrop-blur-sm border border-primary/20 shadow-[0_0_30px_rgb(from_var(--color-primary)_r_g_b_/_0.15)]">
              <span className="material-symbols-outlined text-4xl text-primary drop-shadow-[0_0_8px_var(--color-primary)]">
                watch
              </span>
              {/* Ping Animation Ring */}
              <div className="absolute inset-0 -z-10 animate-ping rounded-full opacity-20 [animation-duration:3s] border border-primary/30" />
            </div>

            {/* Syncing Activity Badge */}
            <div className="flex items-center gap-2 rounded-full bg-black/40 px-3 py-1.5 backdrop-blur-md border border-primary/10">
              <div className="h-1.5 w-1.5 animate-pulse rounded-full bg-primary" />
              <span className="text-[10px] font-bold uppercase tracking-widest text-primary/90">
                Syncing Activity
              </span>
            </div>
          </div>
        </div>

        {/* Bottom Card - Glass Morphism */}
        <div className="group relative w-full pb-4">
          <div className="relative overflow-hidden rounded-[2.5rem] border border-white/[0.08] bg-[#102210]/70 backdrop-blur-[20px] shadow-[0_20px_50px_-12px_rgba(0,0,0,0.8)] transition-all duration-500 hover:shadow-[0_20px_50px_-12px_rgb(from_var(--color-primary)_r_g_b_/_0.15)]">
            {/* Top Gradient Line */}
            <div
              className="absolute inset-x-0 top-0 h-px opacity-80"
              style={{
                background: `linear-gradient(to right, transparent, var(--color-primary), transparent)`,
              }}
            />

            <div className="px-8 pt-6 pb-8">
              {/* Title */}
              <h1 className="mb-3 text-4xl font-bold leading-[1.05] tracking-tight text-white">
                Track <br />
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary to-primary/60">
                  Automatically
                </span>
              </h1>
              {/* Description */}
              <p className="text-base font-light leading-relaxed text-white/70">
                Connect your Strava. We verify your progress in real-time so you
                can focus on the grind.
              </p>
              {/* Bottom Row - Chart & Button */}
              <div className="mt-8 flex items-center justify-between border-t border-white/5 pt-6">
                {/* Mini Bar Chart */}
                <div className="flex h-10 items-end gap-1.5 opacity-80">
                  <div className="w-1.5 h-[30%] rounded-t-full bg-white/10 transition-all duration-700 group-hover:h-[40%]" />
                  <div className="w-1.5 h-[50%] rounded-t-full bg-white/20 transition-all duration-700 delay-75 group-hover:h-[60%]" />
                  <div className="w-1.5 h-[80%] rounded-t-full transition-all duration-700 delay-100 group-hover:h-[70%] bg-primary/60" />
                  <div className="w-1.5 h-full rounded-t-full transition-all duration-700 delay-150 bg-primary shadow-[0_0_10px_var(--color-primary)]" />
                  <div className="w-1.5 h-[45%] rounded-t-full bg-white/20 transition-all duration-700 delay-200 group-hover:h-[55%]" />
                </div>

                {/* Next Button */}
                <button
                  onClick={onNext}
                  className="group/btn relative flex h-16 w-16 items-center justify-center rounded-full text-[#102210] transition-all duration-300 hover:scale-110 active:scale-95 active:bg-white bg-primary"
                >
                  <span className="material-symbols-outlined text-2xl font-semibold transition-transform duration-300 group-hover/btn:translate-x-1 w-6 h-6 flex items-center justify-center">
                    arrow_forward
                  </span>
                  {/* Hover Glow */}
                  <div className="absolute inset-0 -z-10 rounded-full opacity-0 transition-opacity duration-300 group-hover/btn:opacity-100 blur-md bg-primary/30" />
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
